import { useCallback, useEffect, useState } from 'react';
import type {
  AiInfluencer,
  Character,
  CreateAiInfluencerRequest,
  CreateCharacterRequest,
  CreateSoulCastRequest,
  CreateSoulIdentityRequest,
  Job,
  SoulIdentity,
  UpdateAiInfluencerRequest,
  UpdateCharacterRequest,
} from '@volt/shared';
import { dataUrlToBase64, imageFileToDataUrl } from '../lib/composerAttachments.js';
import { useSession } from '../state/session.js';

export interface CharacterState {
  soulIdentities: SoulIdentity[];
  characters: Character[];
  aiInfluencers: AiInfluencer[];
  loading: boolean;
  reload: () => Promise<void>;
  createSoulIdentity: (input: CreateSoulIdentityRequest) => Promise<SoulIdentity>;
  /** Uploads one photo and returns the `attachment:<id>` ref used by createSoulIdentity. */
  uploadSoulPhoto: (file: File) => Promise<string>;
  /** Starts the 3D model build for a ready Soul ID; the list then polls until it lands. */
  startModel3d: (id: string) => Promise<SoulIdentity>;
  createCharacter: (input: CreateCharacterRequest) => Promise<Character>;
  updateCharacter: (id: string, patch: UpdateCharacterRequest) => Promise<Character>;
  deleteCharacter: (id: string) => Promise<void>;
  createAiInfluencer: (input: CreateAiInfluencerRequest) => Promise<AiInfluencer>;
  updateAiInfluencer: (id: string, patch: UpdateAiInfluencerRequest) => Promise<AiInfluencer>;
  deleteAiInfluencer: (id: string) => Promise<void>;
  createSoulCast: (input: CreateSoulCastRequest) => Promise<Job>;
}

export function useCharacter(): CharacterState {
  const { api } = useSession();
  const [soulIdentities, setSoulIdentities] = useState<SoulIdentity[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [aiInfluencers, setAiInfluencers] = useState<AiInfluencer[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [souls, chars, influencers] = await Promise.all([
        api.listSoulIdentities(),
        api.listCharacters(),
        api.listAiInfluencers(),
      ]);
      setSoulIdentities(souls.soulIdentities);
      setCharacters(chars.characters);
      setAiInfluencers(influencers.aiInfluencers);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const createSoulIdentity = useCallback(
    async (input: CreateSoulIdentityRequest) => {
      const { soulIdentity } = await api.createSoulIdentity(input);
      setSoulIdentities((prev) => [soulIdentity, ...prev]);
      return soulIdentity;
    },
    [api],
  );

  const startModel3d = useCallback(
    async (id: string) => {
      const { soulIdentity } = await api.startSoulModel3d(id);
      setSoulIdentities((prev) => prev.map((item) => (item.id === id ? soulIdentity : item)));
      return soulIdentity;
    },
    [api],
  );

  const createCharacter = useCallback(
    async (input: CreateCharacterRequest) => {
      const { character } = await api.createCharacter(input);
      setCharacters((prev) => [character, ...prev]);
      return character;
    },
    [api],
  );

  const updateCharacter = useCallback(
    async (id: string, patch: UpdateCharacterRequest) => {
      const { character } = await api.updateCharacter(id, patch);
      setCharacters((prev) => prev.map((item) => (item.id === id ? character : item)));
      return character;
    },
    [api],
  );

  const deleteCharacter = useCallback(
    async (id: string) => {
      await api.deleteCharacter(id);
      setCharacters((prev) => prev.filter((item) => item.id !== id));
    },
    [api],
  );

  const createAiInfluencer = useCallback(
    async (input: CreateAiInfluencerRequest) => {
      const { aiInfluencer } = await api.createAiInfluencer(input);
      setAiInfluencers((prev) => [aiInfluencer, ...prev]);
      return aiInfluencer;
    },
    [api],
  );

  const updateAiInfluencer = useCallback(
    async (id: string, patch: UpdateAiInfluencerRequest) => {
      const { aiInfluencer } = await api.updateAiInfluencer(id, patch);
      setAiInfluencers((prev) => prev.map((item) => (item.id === id ? aiInfluencer : item)));
      return aiInfluencer;
    },
    [api],
  );

  const deleteAiInfluencer = useCallback(
    async (id: string) => {
      await api.deleteAiInfluencer(id);
      setAiInfluencers((prev) => prev.filter((item) => item.id !== id));
    },
    [api],
  );

  const uploadSoulPhoto = useCallback(
    async (file: File) => {
      const { dataUrl, fileName, mime } = await imageFileToDataUrl(file);
      const { attachment } = await api.uploadAttachment({
        fileName,
        mime,
        dataBase64: dataUrlToBase64(dataUrl),
      });
      return `attachment:${attachment.id}`;
    },
    [api],
  );

  const hasPending = soulIdentities.some(
    (item) => item.status === 'training' || item.model3dStatus === 'processing',
  );
  useEffect(() => {
    if (!hasPending) return undefined;
    const timer = setInterval(() => {
      api
        .listSoulIdentities()
        .then((result) => setSoulIdentities(result.soulIdentities))
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(timer);
  }, [api, hasPending]);

  const createSoulCast = useCallback(
    (input: CreateSoulCastRequest) => api.createSoulCast(input),
    [api],
  );

  return {
    soulIdentities,
    characters,
    aiInfluencers,
    loading,
    reload,
    createSoulIdentity,
    uploadSoulPhoto,
    startModel3d,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    createAiInfluencer,
    updateAiInfluencer,
    deleteAiInfluencer,
    createSoulCast,
  };
}
